import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioRecorderState = 'idle' | 'starting' | 'recording';

/**
 * Tap-to-start / tap-to-stop voice notes for the diary forms.
 *
 * Both forms used to hold their own copy of this as a press-and-hold button
 * (`onMouseDown`/`onTouchStart` to start, `onMouseUp`/`onTouchEnd` to stop), and
 * both were unstoppable in practice, for two reasons that compound:
 *
 * 1. Opening the microphone is asynchronous. `getUserMedia` resolves tens to
 *    hundreds of milliseconds after the press, and the old code only published
 *    the recorder to its ref *after* the await. A release that landed inside that
 *    window — i.e. any ordinary tap — found a null ref, did nothing, and returned;
 *    the recorder then started with nobody holding it and ran on forever. The
 *    next tap's release stopped that orphan, so every other tap looked like
 *    "stopped for an instant, then started recording again".
 * 2. A press/release pair on a touchscreen produces two event pairs (the real
 *    touch one, then a synthetic mouse one), so the same physical tap ran the
 *    start/stop handlers twice.
 *
 * A single `toggle()` driven by `onClick` removes (2) outright — one click event
 * per press, whatever the input device — and an explicit `starting` state removes
 * (1): a stop that arrives while the mic is still opening marks the pending start
 * abandoned, and the stream is torn down the moment it arrives instead of being
 * silently adopted.
 */
export function useAudioRecorder(onRecorded: (blob: Blob) => void, onError: (message: string) => void) {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  /** Set when a stop lands before `getUserMedia` resolves — see (1) above. */
  const abandonedRef = useRef(false);
  /** Kept in a ref so the unmount cleanup never re-runs on a changing callback. */
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [state, setState] = useState<AudioRecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (state !== 'recording') return;
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [state]);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    setState('idle');
    // `onstop` builds the blob and releases the stream; calling stop() twice on
    // an already-inactive recorder throws, hence the state check.
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    else releaseStream();
  }, [releaseStream]);

  const start = useCallback(async () => {
    abandonedRef.current = false;
    setState('starting');
    setElapsed(0);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setState('idle');
      onErrorRef.current('Không thể truy cập micro — kiểm tra quyền truy cập của trình duyệt');
      return;
    }
    // The user let go (or the form closed) while the mic was opening: this
    // recording was never wanted, so it must not start behind their back.
    if (abandonedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    streamRef.current = stream;

    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (event) => event.data.size > 0 && chunksRef.current.push(event.data);
    recorder.onstop = () => {
      releaseStream();
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      chunksRef.current = [];
      if (blob.size > 0) onRecordedRef.current(blob);
    };
    recorder.onerror = () => {
      recorderRef.current = null;
      chunksRef.current = [];
      releaseStream();
      setState('idle');
      onErrorRef.current('Ghi âm thất bại, vui lòng thử lại');
    };
    recorder.start();
    recorderRef.current = recorder;
    setState('recording');
  }, [releaseStream]);

  const toggle = useCallback(() => {
    if (state === 'starting') {
      // Cancel a start still waiting on the microphone.
      abandonedRef.current = true;
      setState('idle');
      return;
    }
    if (state === 'recording') stop();
    else void start();
  }, [state, start, stop]);

  // Leaving the form mid-recording must not leave the microphone live.
  useEffect(
    () => () => {
      abandonedRef.current = true;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    },
    [],
  );

  return { state, elapsed, toggle, recording: state === 'recording' };
}
